import type { WorkerModuleScorecard, WorkerModuleStatRow } from '@insolitum/types';
import { foldStatRows, groupStatRowsByWorker, workerModuleAccess } from '@insolitum/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { supabase } from '../lib/supabase';
import { moduleWorkerRoute, WORKER_MODULE_SLUGS } from '../modules/registry';
import { useAuth } from '../providers/AuthProvider';

/**
 * Przekrój pracowników przez organy — strona powłoki.
 * =================================================
 *
 * Cała arytmetyka siedzi w `universe_worker_module_stats` (migracja 442); ten
 * plik tylko ją woła i zwija wynik przez `foldStatRows` z `@insolitum/utils`.
 * Ta sama funkcja bazodanowa zasila zakładki Pracownicy w modułach, więc karta
 * w Temperatio i kolumna „Temperatio" w kartotece pokazują z definicji tę samą
 * liczbę — rozjazd, od którego się zaczęło, nie ma już gdzie powstać.
 *
 * Powłoka NIE MA `@tanstack/react-query` (mają go moduły), więc trzymamy się
 * miejscowego wzorca `useState` + `useEffect` + jawny `refresh`, jak
 * `useModuleSubscription` czy `useDashboardStats`.
 */

/** Kolumny kartoteki — jawna lista zamiast `select('*')`. */
const WORKER_COLUMNS = [
  'id',
  'first_name',
  'last_name',
  'position',
  'email',
  'phone',
  'is_active',
  'avatar_url',
  'last_login_at',
  'hire_date',
  'tenure_start_date',
  'worker_role',
  'experience_level',
  'preferred_language',
  'preferences',
  'created_at',
].join(', ');
// DLACZEGO jawna lista: `select('*')` na `shop_floor_workers` wysyła do
// przeglądarki `pin_hash`, `hourly_rate_*`, `monthly_salary_*`,
// `compensation_rate`, `id_document_number` i `face_encoding` — dla KAŻDEGO
// pracownika, przy każdym wejściu na listę. RLS na to pozwala (to nie dziura
// w politykach, tylko nadmiarowy odczyt), ale kartoteka potrzebuje kilkunastu
// kolumn, nie sześćdziesięciu, a wektor twarzy nie ma czego szukać w liście.

/** Wiersz pracownika w kartotece — dokładnie to, co pobiera `WORKER_COLUMNS`. */
export interface CatalogWorker {
  id: string;
  first_name: string;
  last_name: string;
  position: string | null;
  email: string | null;
  phone: string | null;
  is_active: boolean;
  avatar_url: string | null;
  last_login_at: string | null;
  hire_date: string | null;
  tenure_start_date: string | null;
  worker_role: string | null;
  experience_level: string | null;
  preferred_language: string | null;
  preferences: { module_access?: unknown; [key: string]: unknown } | null;
  created_at: string;
}

/** Pracownik razem z jego przekrojem przez organy. */
export interface CatalogRow {
  worker: CatalogWorker;
  /** Po jednej karcie na każdy organ z `WORKER_MODULE_SLUGS` — także pusty. */
  modules: WorkerModuleScorecard[];
  /** Efektywne przypisania (`preferences.module_access` albo permisywny default). */
  accessSlugs: string[];
}

export const WORKER_WINDOW_OPTIONS = [7, 30, 90, 365] as const;
export type WorkerWindowDays = (typeof WORKER_WINDOW_OPTIONS)[number];

export const WORKER_WINDOW_LABELS: Record<WorkerWindowDays, string> = {
  7: '7 dni',
  30: '30 dni',
  90: '90 dni',
  365: 'rok',
};

interface UseWorkerCatalogResult {
  rows: CatalogRow[];
  isLoading: boolean;
  /** Ładowanie samych metryk — lista jest już widoczna, liczby jeszcze nie. */
  isLoadingStats: boolean;
  error: string | null;
  /** `true`, gdy lista jest, ale RPC z metrykami padło — kartoteka degraduje się do samych kartotek. */
  statsUnavailable: boolean;
  refresh: () => Promise<void>;
}

function deepLinkFor(slug: string): string | null {
  return moduleWorkerRoute(slug);
}

/**
 * Cała załoga + jej wyniki we wszystkich organach.
 *
 * Metryki jadą OSOBNYM zapytaniem i osobnym stanem ładowania: gdy RPC padnie
 * albo zwolni, lista pracowników i tak się pokaże. Kartoteka kadrowa ma
 * działać także wtedy, gdy jeden organ ma zły dzień — inaczej awaria agregatu
 * zabiera cały ekran zamiast kilku liczb.
 */
export function useWorkerCatalog(windowDays: number = 30): UseWorkerCatalogResult {
  const { currentOrganization } = useAuth();
  const organizationId = currentOrganization?.organization_id ?? null;

  const [workers, setWorkers] = useState<CatalogWorker[]>([]);
  const [statRows, setStatRows] = useState<WorkerModuleStatRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statsUnavailable, setStatsUnavailable] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!organizationId) {
      setWorkers([]);
      setStatRows([]);
      setIsLoading(false);
      setIsLoadingStats(false);
      return;
    }

    setIsLoading(true);
    setIsLoadingStats(true);
    setError(null);
    setStatsUnavailable(false);

    const [rosterResult, statsResult] = await Promise.all([
      supabase
        .from('shop_floor_workers')
        .select(WORKER_COLUMNS)
        .eq('organization_id', organizationId)
        .order('last_name'),
      supabase.rpc('universe_worker_module_stats', {
        p_organization_id: organizationId,
        p_worker_id: null,
        p_module_slug: null,
        p_days: windowDays,
      }),
    ]);

    if (rosterResult.error) {
      console.error('[useWorkerCatalog] roster', rosterResult.error);
      setError('Nie udało się załadować pracowników');
      setWorkers([]);
    } else {
      setWorkers((rosterResult.data ?? []) as unknown as CatalogWorker[]);
    }
    setIsLoading(false);

    if (statsResult.error) {
      console.error('[useWorkerCatalog] stats', statsResult.error);
      setStatsUnavailable(true);
      setStatRows([]);
    } else {
      setStatRows((statsResult.data ?? []) as WorkerModuleStatRow[]);
    }
    setIsLoadingStats(false);
  }, [organizationId, windowDays]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  const rows = useMemo<CatalogRow[]>(() => {
    const byWorker = groupStatRowsByWorker(statRows);
    return workers.map((worker) => {
      const accessSlugs = workerModuleAccess(worker.preferences, WORKER_MODULE_SLUGS);
      return {
        worker,
        accessSlugs,
        modules: foldStatRows(byWorker.get(worker.id) ?? [], {
          allSlugs: WORKER_MODULE_SLUGS,
          accessSlugs,
          windowDays,
          deepLink: deepLinkFor,
        }),
      };
    });
  }, [workers, statRows, windowDays]);

  return { rows, isLoading, isLoadingStats, error, statsUnavailable, refresh: fetchAll };
}

interface UseWorkerDossierResult {
  row: CatalogRow | null;
  isLoading: boolean;
  error: string | null;
  statsUnavailable: boolean;
  refresh: () => Promise<void>;
}

/** Jedna kartoteka — ten sam kontrakt, zawężony do jednego pracownika. */
export function useWorkerDossier(
  workerId: string | undefined,
  windowDays: number = 30
): UseWorkerDossierResult {
  const { currentOrganization } = useAuth();
  const organizationId = currentOrganization?.organization_id ?? null;

  const [worker, setWorker] = useState<CatalogWorker | null>(null);
  const [statRows, setStatRows] = useState<WorkerModuleStatRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statsUnavailable, setStatsUnavailable] = useState(false);

  const fetchOne = useCallback(async () => {
    if (!organizationId || !workerId) {
      setWorker(null);
      setStatRows([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    setStatsUnavailable(false);

    const [workerResult, statsResult] = await Promise.all([
      supabase
        .from('shop_floor_workers')
        .select(WORKER_COLUMNS)
        // Filtr po organizacji zostaje MIMO filtra po id: bez niego link
        // z cudzym UUID opierałby się wyłącznie na RLS. Polityka faktycznie
        // by go odrzuciła, ale kartoteka nie powinna być jedynym miejscem,
        // w którym o tym pamiętamy.
        .eq('organization_id', organizationId)
        .eq('id', workerId)
        .maybeSingle(),
      supabase.rpc('universe_worker_module_stats', {
        p_organization_id: organizationId,
        p_worker_id: workerId,
        p_module_slug: null,
        p_days: windowDays,
      }),
    ]);

    if (workerResult.error) {
      console.error('[useWorkerDossier] worker', workerResult.error);
      setError('Nie udało się załadować kartoteki pracownika');
      setWorker(null);
    } else {
      setWorker((workerResult.data ?? null) as unknown as CatalogWorker | null);
    }

    if (statsResult.error) {
      console.error('[useWorkerDossier] stats', statsResult.error);
      setStatsUnavailable(true);
      setStatRows([]);
    } else {
      setStatRows((statsResult.data ?? []) as WorkerModuleStatRow[]);
    }

    setIsLoading(false);
  }, [organizationId, workerId, windowDays]);

  useEffect(() => {
    void fetchOne();
  }, [fetchOne]);

  const row = useMemo<CatalogRow | null>(() => {
    if (!worker) return null;
    const accessSlugs = workerModuleAccess(worker.preferences, WORKER_MODULE_SLUGS);
    return {
      worker,
      accessSlugs,
      modules: foldStatRows(statRows, {
        allSlugs: WORKER_MODULE_SLUGS,
        accessSlugs,
        windowDays,
        deepLink: deepLinkFor,
      }),
    };
  }, [worker, statRows, windowDays]);

  return { row, isLoading, error, statsUnavailable, refresh: fetchOne };
}

/**
 * Zmiana przypisania pracownika do organu.
 *
 * Idzie przez RPC, a nie przez `update({ preferences })`, bo PostgREST
 * podmienia CAŁY obiekt `preferences`. Dopóki przypisanie zapisywał jeden
 * modal, kolizja była teoretyczna; teraz plakietkę da się kliknąć z kartoteki,
 * z listy i z każdej zakładki Pracownicy, więc dwa równoległe zapisy skasowałyby
 * sobie nawzajem klucze, których żaden z nich nie dotykał (`language`,
 * `notifications_enabled`). `jsonb_set` po stronie bazy jest atomowy.
 *
 * Zwraca listę PO zapisie — wołający nadpisuje nią swój optymistyczny stan
 * zamiast zgadywać, co wyszło.
 */
export async function setWorkerModuleAccess(
  workerId: string,
  slugs: string[],
  mode: 'set' | 'add' | 'remove' = 'set'
): Promise<string[]> {
  const { data, error } = await supabase.rpc('universe_worker_module_access_set', {
    p_worker_id: workerId,
    p_module_slugs: slugs,
    p_mode: mode,
    p_all_slugs: WORKER_MODULE_SLUGS,
  });
  if (error) throw error;
  return (data ?? []) as string[];
}
